<!--
	Copyright (c) 2017-present, flop! contributors
	SPDX-License-Identifier: AGPL-3.0-or-later
	https://framagit.org/flopedt/flopedt
-->

<script setup lang="ts">
import { DropdownMenuItem as RekaDropdownMenuItem } from 'reka-ui'

import { RouteNames } from '@/router/routes.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { useDepartmentsStore } from '@/stores/departments/departments.ts'

import DropdownMenuGroup from '@uikit/menu/dropdown/DropdownMenuGroup.vue'
import DropdownMenuItem from '@uikit/menu/dropdown/DropdownMenuItem.vue'
import DropdownMenuLabel from '@uikit/menu/dropdown/DropdownMenuLabel.vue'
import DropdownMenuSeparator from '@uikit/menu/dropdown/DropdownMenuSeparator.vue'
import DropdownMenu from '@uikit/menu/DropdownMenu.vue'
import AccountMenuItems from '@/components/header/account/AccountMenuItems.vue'

import TransferIcon from '~mingcute/arrow/transfer_line.svg'
import SchoolIcon from '~mingcute/building/school_line.svg'
import ExitIcon from '~mingcute/system/exit_line.svg'
import SettingsIcon from '~mingcute/system/settings_2_line.svg'
import SettingsDeptIcon from '~mingcute/system/settings_4_line.svg'
import ToolIcon from '~mingcute/system/tool_line.svg'
import UserIcon from '~mingcute/user/user_4_line.svg'

const authStore = useAuthStore()
const deptStore = useDepartmentsStore()
</script>

<template>
  <template v-if="!authStore.isAuthenticated">
    <RouterLink :to="{ name: RouteNames.LOGIN }" class="account-button">
      <UserIcon />
      <span>{{ $t('authentication.login') }}</span>
    </RouterLink>
    <DropdownMenu :side-offset="4">
      <template #items>
        <RouterLink :to="{ name: RouteNames.LOGIN }">
          <DropdownMenuItem>
            <template #icon><UserIcon /></template>
            <span>{{ $t('authentication.login') }}</span>
          </DropdownMenuItem>
        </RouterLink>
        <AccountMenuItems />
      </template>
    </DropdownMenu>
  </template>
  <template v-else>
    <DropdownMenu as-child :side-offset="4">
      <template #trigger>
        <button class="account-button" :aria-label="$t('header.user-menu')">
          <UserIcon />
          <span class="name">{{ authStore.user.getFullName() }}</span>
        </button>
      </template>
      <template #items>
        <RouterLink :to="{ name: RouteNames.MY_ACCOUNT }">
          <RekaDropdownMenuItem class="account-info">
            <UserIcon class="account-icon" />
            <div class="account-details">
              <span class="account-name">{{ authStore.user.getFullName() }}</span>
              <!-- TODO: proper role display -->
              <span class="account-role">{{ $t('header.role.admin') }}</span>
            </div>
          </RekaDropdownMenuItem>
        </RouterLink>
        <DropdownMenuSeparator />
        <RouterLink :to="{ name: RouteNames.MY_ACCOUNT_SETTINGS }">
          <DropdownMenuItem>
            <template #icon><SettingsIcon /></template>
            <span>{{ $t('header.settings') }}</span>
          </DropdownMenuItem>
        </RouterLink>
        <DropdownMenuItem danger @select="authStore.logout()">
          <template #icon><ExitIcon /></template>
          <span>{{ $t('authentication.logout') }}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />

        <DropdownMenuGroup v-if="deptStore.isCurrentDepartmentSelected">
          <DropdownMenuLabel>
            {{ $t('header.department-label', { department: deptStore.current.name }) }}
          </DropdownMenuLabel>
          <RouterLink :to="{ name: RouteNames.DEPARTMENT_SELECTION }">
            <DropdownMenuItem>
              <template #icon><TransferIcon /></template>
              <span>{{ $t('header.change-department') }}</span>
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink :to="{ name: RouteNames.DEPARTMENT_MANAGEMENT_GENERATION }">
            <DropdownMenuItem>
              <template #icon><ToolIcon /></template>
              <span>{{ $t('header.manage-generation') }}</span>
            </DropdownMenuItem>
          </RouterLink>
          <RouterLink :to="{ name: RouteNames.DEPARTMENT_MANAGEMENT }">
            <DropdownMenuItem>
              <template #icon><SettingsDeptIcon /></template>
              <span>{{ $t('header.manage-department') }}</span>
            </DropdownMenuItem>
          </RouterLink>

          <DropdownMenuSeparator />
        </DropdownMenuGroup>

        <RouterLink :to="{ name: RouteNames.MANAGEMENT }">
          <DropdownMenuItem>
            <template #icon><SchoolIcon /></template>
            <span>{{ $t('header.manage') }}</span>
          </DropdownMenuItem>
        </RouterLink>
        <AccountMenuItems />
      </template>
    </DropdownMenu>
  </template>
</template>

<style scoped>
.account-button {
  all: unset;
  box-sizing: border-box;
  min-height: 0;
  min-width: 0;
  flex-shrink: 0;

  display: flex;
  gap: 0.5rem;

  cursor: pointer;
  font-weight: 600;
  border-radius: 2px;
}

.account-button:focus-visible {
  outline: 3px var(--color-outline) solid;
  outline-offset: 4px;
}

.account-button svg {
  width: 1.125em;
  height: 1.125em;
}

.name {
  color: var(--color-text-secondary);
}

.account-info {
  display: flex;
  align-items: center;
  padding: 0.25rem;
  gap: 0.5rem;
  margin: 2px;

  cursor: pointer;
  border-radius: var(--border-radius-small);
  overflow: visible;
}

.account-info:focus {
  outline: 2px transparent solid;
  box-shadow: 0 0 0 2px var(--color-outline);
  background-color: var(--color-background-accent-light);
}

.account-icon {
  height: 2em;
  width: 2em;
}

.account-name {
  font-weight: 500;
}

.account-details {
  display: flex;
  flex-direction: column;
}

.account-role {
  font-size: 0.75rem;
  color: var(--color-text-muted);
}
</style>
